import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AppStateStore } from "../core/sim/app-state.store";
import { AudioService } from "../core/audio/audio.service";
import { BoatControlsStore } from "../core/sim/boat-controls.store";
import { SimService } from "../core/sim/sim.service";
import {
  SUPPORTED_LOCALES,
  type CastOffProLocale,
} from "../core/i18n/i18n.providers";
import {
  CASTOFFPRO_PLAYER_BOAT_ID,
  CASTOFFPRO_RENDER_QUALITY,
  CASTOFFPRO_TERRAIN_ID,
} from "../core/storage/storage-keys";
import type { AppState, HudNumericField } from "../../simulation-core";

/**
 * Slide-out service drawer with environmental + debug sliders. Replaces
 * the inline panels from the original mega-AppComponent. Toggled from the
 * top-bar; defaults to closed.
 */
@Component({
  selector: "po-settings-drawer",
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./settings-drawer.component.html",
  styleUrl: "./settings-drawer.component.css",
})
export class SettingsDrawerComponent {
  private readonly stateStore = inject(AppStateStore);
  private readonly controls = inject(BoatControlsStore);
  private readonly audio = inject(AudioService);
  private readonly sim = inject(SimService);
  private readonly translate = inject(TranslateService);

  readonly availableLocales = SUPPORTED_LOCALES;
  readonly currentLocale = signal<CastOffProLocale>(
    (this.translate.currentLang as CastOffProLocale | undefined) ??
      (this.translate.getDefaultLang() as CastOffProLocale | undefined) ??
      "pl",
  );

  readonly open = signal(false);
  readonly state = this.stateStore.state;
  readonly terrainPresets = computed(() => {
    // Touch sim.handle so the list refreshes once the sim is ready.
    this.sim.handle();
    return this.sim.listTerrainPresets();
  });
  readonly boatChoices = computed(() => {
    this.sim.handle();
    return this.sim.listBoatChoices();
  });
  readonly selectedBoatId = computed(() => {
    this.boatChoices();
    return this.state().playerBoatId;
  });
  readonly throttle = computed(() => this.controls.throttle());
  readonly steer = this.controls.steer;
  readonly audioMuted = this.audio.muted;
  readonly audioVolume = this.audio.volume;
  readonly newGame = output<void>();

  toggle(): void {
    this.open.update((v) => !v);
  }

  close(): void {
    this.open.set(false);
  }

  openNewGame(): void {
    this.close();
    this.newGame.emit();
  }

  updateNumber(key: HudNumericField, event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.stateStore.patch({ [key]: value } as Partial<AppState>);
  }

  updateCamera(event: Event): void {
    const cameraMode = (event.target as HTMLSelectElement)
      .value as AppState["cameraMode"];
    this.stateStore.patch({ cameraMode });
  }

  updateTerrain(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    if (!id || id === this.state().terrainId) return;
    // Heightmap + dock geometry are baked at construction — swap presets
    // by persisting the choice and reloading, matching the legacy lil-gui
    // behaviour from `simulation-core/main.ts`.
    localStorage.setItem(CASTOFFPRO_TERRAIN_ID, id);
    location.reload();
  }

  updateBoat(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    if (!id || id === this.state().playerBoatId) return;
    localStorage.setItem(CASTOFFPRO_PLAYER_BOAT_ID, id);
    location.reload();
  }

  updateRenderQuality(event: Event): void {
    const value = (event.target as HTMLSelectElement)
      .value as AppState["renderQuality"];
    if (!value || value === this.state().renderQuality) return;
    // Shadow-map size + SSAO targets are baked at construction in main.ts,
    // so a quality change is applied by persisting the choice and reloading
    // — same pattern as terrain / boat selection.
    localStorage.setItem(CASTOFFPRO_RENDER_QUALITY, value);
    location.reload();
  }

  toggleWindStreaks(event: Event): void {
    const showWindStreaks = (event.target as HTMLInputElement).checked;
    this.stateStore.patch({ showWindStreaks });
  }

  toggleBirds(event: Event): void {
    const showBirds = (event.target as HTMLInputElement).checked;
    this.stateStore.patch({ showBirds });
  }

  toggleCollisionFootprint(event: Event): void {
    const showCollisionFootprint = (event.target as HTMLInputElement).checked;
    this.stateStore.patch({ showCollisionFootprint });
  }

  toggleStaticColliders(event: Event): void {
    const showStaticColliders = (event.target as HTMLInputElement).checked;
    this.stateStore.patch({ showStaticColliders });
  }

  releaseBirds(): void {
    if (!this.state().showBirds) {
      this.stateStore.patch({ showBirds: true });
    }
    this.sim.releaseBirds();
  }

  updateThrottle(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.controls.setLever(value);
  }

  updateSteer(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.controls.setSteer(value);
  }

  releaseControls(): void {
    this.controls.release();
  }

  toggleAudio(): void {
    this.audio.toggleMute();
  }

  updateVolume(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.audio.setVolume(value);
  }

  updateLocale(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as CastOffProLocale;
    if (!SUPPORTED_LOCALES.includes(value)) return;
    if (value === this.currentLocale()) return;
    this.translate.use(value);
    this.currentLocale.set(value);
  }
}
